import { initA11YTabs } from './partials/_tabs';
import { splitRange } from '../../ts/helper';

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-tariff-calculator"]';

/**
 * Friendation component
 */
export const blockTariffCalculator = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    const calculatorWrapper: HTMLElement =
      component.querySelector('.calculator');

    const handleTabs = () => {
      initA11YTabs(calculatorWrapper);
    };

    /** The Tariff Calculator has some unique range sliders that control the value of another input in a form */
    const handleRangeSlider = () => {
      const forms: HTMLFormElement[] = Array.from(
        calculatorWrapper.querySelectorAll('form')
      );

      forms.forEach((form) => {
        /** The range slider controls a {@link field} */
        const rangeSliderWrapper: HTMLDivElement =
          form.querySelector('.rangeSlider');

        if (rangeSliderWrapper) {
          const rangeSliderInput: HTMLInputElement =
            rangeSliderWrapper.querySelector('input[type="range"]');

          const fieldName = rangeSliderInput.dataset.name;

          /** The range of the {@link rangeSliderInput} */
          const range = {
            min: parseInt(rangeSliderInput.min),
            max: parseInt(rangeSliderInput.max)
          };

          /** The field that is controlled by the {@link rangeSliderWrapper} */
          const field: HTMLInputElement = form.querySelector(
            `[name="${fieldName}"]`
          );

          /** Sync {@link field} with {@link rangeSliderInput} */
          field.addEventListener('change', () => {
            /** The {@link field} value can be higher than the max value on {@link rangeSliderInput}. So the rangSlider only updates, when it can actually display the value. Otherwise it would reset the {@link field} value again – which we do not want. */
            if (parseInt(field.value) <= parseInt(rangeSliderInput.max)) {
              rangeSliderInput.value = field.value;
            }
          });

          /** Sync {@link rangeSliderInput} with {@link field} */
          rangeSliderInput.addEventListener('input', () => {
            field.value = rangeSliderInput.value;
          });

          /** Each control button will set the value of both {@link rangeSliderInput} and {@link field} to a portion of the available {@link range}. Clicking on the first would set the value to the 1/x where x is the available range. Clicking on the second would result in the 2/x and so on. */
          const controlButtons: HTMLButtonElement[] = Array.from(
            rangeSliderWrapper.querySelectorAll('.rangeSlider-icons-button')
          );

          const stepSize = parseInt(rangeSliderInput.step);
          const rangeParts = splitRange({
            min: range.min,
            max: range.max,
            splitBy: controlButtons.length,
            stepSize
          });

          controlButtons.forEach((controlButton, i) => {
            controlButton.addEventListener('click', (e: MouseEvent) => {
              e.preventDefault();

              const value = rangeParts[i].toString();

              rangeSliderInput.value = value;
              field.value = value;
            });
          });
        }
      });
    };

    /**
     * Modify the data that is being submitted.
     */
    const handleSubmit = () => {
      const forms: HTMLFormElement[] = Array.from(
        component.querySelectorAll('form')
      );

      forms.forEach((form) => {
        form.addEventListener('submit', (e: SubmitEvent) => {
          /** Prevent submit event */
          e.preventDefault();

          /** The form data */
          const formData = new FormData(form);

          /** When `<input name="doubleTariff">` is checked, we need to set „meterCnt” to `"2"` */
          const input: HTMLInputElement =
            form.querySelector('[name="meterCnt"]');
          if (input) input.value = formData.has('doubleTariff') ? '2' : '1';

          /** Submit */
          form.submit();
        });
      });
    };

    /**
     * In the real world a zip code has only 1 city. In the Stadtwerke Hilden Universe, the zip code `40721` can be `"Hilden"` or `"Düsseldorf"`. We need to display an additional `HTMLSelectElement`, when the user chooses `40721` as postal code.
     */
    const handleZipCodeCity = () => {
      const forms: HTMLFormElement[] = Array.from(
        calculatorWrapper.querySelectorAll('form')
      );

      forms.forEach((form) => {
        const postalCodeInput: HTMLSelectElement = form.querySelector(
          '[name="postalCode"]'
        );
        const citySelect: HTMLSelectElement =
          form.querySelector('[name="city"]');
        const cityNamesCount: HTMLSelectElement = form.querySelector(
          '[name="cityNamesCount"]'
        );
        if (!postalCodeInput || !citySelect || !cityNamesCount) return;

        /**
         * Show the city select, when to postal code is `"40721"`
         */
        const updateZipCodeCity = () => {
          const isDouble = postalCodeInput.value === '40721';
          citySelect.disabled = !isDouble;
          cityNamesCount.value = isDouble ? '2' : '1';
        };

        postalCodeInput.addEventListener('input', updateZipCodeCity);
        updateZipCodeCity();
      });
    };

    handleTabs();
    handleRangeSlider();
    handleSubmit();
    handleZipCodeCity();
  });
};
